import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  CONTENT_TYPES,
  getMediaValue,
  getPlayerCoverImage,
  useContentfulEntry,
  VideoPlayer,
} from '@livingsecurity/shared';

import { useStandalonePlayerCheck } from '../../hooks/useQueryParams';
import 'cloudinary-video-player/dist/cld-video-player.css';

const DEFAULT_VIDEO_DELIVERY_MODE = 'hls';
const VIDEO_SOURCE_TYPES_BY_DELIVERY_MODE = {
  hls: ['hls'],
  mp4: ['mp4'],
};

const VideoWrapper = styled.div`
  .video-js.vjs-4-3,
  .video-js.vjs-16-9,
  .video-js.vjs-fluid {
    width: 100%;
    max-width: 100%;
    height: 100% !important;
  }

  #cld-video-player {
    opacity: 0;
    transition: 300ms;
  }

  .cld-video-player-dimensions.vjs-fluid {
    padding: 0 !important;
  }

  .vjs-cloudinary-button {
    display: ${({ hideLogo }) => (hideLogo ? 'none !important' : '')};
  }

  width: 100%;
  height: 100%;
`;

const VideoView = ({ contentId, data, type, onPercentChange, playerConfigParams, disableSeeking, refTime, flags }) => {
  const videoPlayerRef = useRef(null);
  const hasMountedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { hideVideoPlayerLogo } = flags;
  const { isStandalonePlayer, isLMS } = useStandalonePlayerCheck();
  const useProgressiveMp4 = isStandalonePlayer || isLMS;
  const videoData = type === CONTENT_TYPES.MODULE ? data.video : data.videoFile?.[0];
  const videoType = type === CONTENT_TYPES.MODULE ? 'video' : 'videoFile';
  const [currentCaptions, setCurrentCaptions] = useState(data?.captionFile);

  const coverImage = useMemo(() => getPlayerCoverImage(data?.coverImage)?.coverImage, [data?.coverImage]);
  const player = videoPlayerRef.current?.getInstance();

  if (!data || type !== CONTENT_TYPES.VIDEO) return null;

  // ESD-3222 - If videoData is not available, we need to get the public ID from the English content as a fallback
  const { data: contentData } = useContentfulEntry({
    id: contentId,
    enabled: !videoData || error,
  });

  const englishVideoData = contentData?.fields?.videoFile?.[0];
  const englishPublicId = useMemo(() => getMediaValue(englishVideoData, videoType)?.publicID, [englishVideoData]);

  const publicId = useMemo(() => getMediaValue(videoData, videoType)?.publicID, [data?.video, data?.videoFile]);
  const videoDeliveryMode = data?.videoDeliveryMode || contentData?.fields?.videoDeliveryMode || DEFAULT_VIDEO_DELIVERY_MODE;
  const sourceTypes =
    VIDEO_SOURCE_TYPES_BY_DELIVERY_MODE[videoDeliveryMode] ||
    VIDEO_SOURCE_TYPES_BY_DELIVERY_MODE[DEFAULT_VIDEO_DELIVERY_MODE];

  player?.on('error', (error) => {
    setError(true);
  });

  useEffect(() => {
    // On back-to-back videos we need to force the player to re-mount and set new cover image.
    // Skip the very first mount — otherwise Chrome's MSE-based HLS gets a half-initialized
    // SourceBuffer from init #1, then re-inits 500ms later, producing a ~14s freeze before
    // playback recovers (Safari is unaffected because it uses native HLS).
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      // currentCaptions is already seeded via useState(data?.captionFile) on first render,
      // so no caption update is needed here.
      return;
    }
    onReset(500, data);
  }, [data]);

  const onReset = useCallback(
    (timeout = 1000, data) => {
      setLoading(true);

      if (data?.captionFile !== currentCaptions) {
        setCurrentCaptions(data?.captionFile);
      }

      setTimeout(() => {
        setLoading(false);
      }, timeout);
    },
    [currentCaptions],
  );

  return (
    <VideoWrapper hideLogo={hideVideoPlayerLogo}>
      {!loading && (
        <VideoPlayer
          ref={videoPlayerRef}
          publicId={englishPublicId || publicId}
          captions={currentCaptions}
          onPercentChange={onPercentChange}
          playerConfigParams={playerConfigParams}
          disableSeeking={disableSeeking}
          refTime={refTime}
          sourceTypes={sourceTypes}
          width={600}
          onReset={onReset}
          wrapperStyles={{
            width: '97%', // TRAIN-1779 Video is shaking
            height: '100%',
            margin: '0 auto',
          }}
          playerOptions={{
            fluid: true,
            controls: true,
            hideContextMenu: false,
            showJumpControls: true,
            playedEventPercents: [25, 50, 75, 90, 98, 100],
            playbackRates: [0.5, 0.75, 1, 1.5],
            logoOnclickUrl: 'https://livingsecurity.com',
            logoImageUrl: 'https://assets.livingsecurity.com/image/upload/c_scale,h_20/v1692841537/LS-Logo_smmosb.png',
            colors: { base: '#000a10', accent: '#B92E2A', text: '#fff' },
            posterOptions: {
              publicId: coverImage ? `${coverImage}.jpg` : null,
            },
            ...(playerConfigParams || {}),
          }}
        />
      )}
    </VideoWrapper>
  );
};

VideoView.propTypes = {
  data: PropTypes.shape(),
  type: PropTypes.oneOf(['video', 'videoFile']).isRequired,
  onPercentChange: PropTypes.func,
  playerConfigParams: PropTypes.shape(),
  disableSeeking: PropTypes.bool,
  refTime: PropTypes.number,
};

VideoView.defaultProps = {
  data: null,
  onPercentChange: null,
  playerConfigParams: {},
  disableSeeking: false,
  refTime: 0,
};

export default VideoView;
